import { Check } from "lucide-react";

export const FieldLabel = ({ children, optional, hint }) => (
  <div className="mb-4">
    <div className="flex items-baseline gap-3">
      <span className="font-serif text-xl font-light text-cream sm:text-2xl">{children}</span>
      {optional && (
        <span className="font-sans text-[0.65rem] uppercase tracking-[0.24em] text-sand">
          Optional
        </span>
      )}
    </div>
    {hint && <p className="mt-2 font-sans text-sm leading-relaxed text-sand">{hint}</p>}
  </div>
);

export const FieldError = ({ children }) =>
  children ? (
    <p className="mt-3 font-sans text-sm text-teal" data-testid="field-error">
      {children}
    </p>
  ) : null;

// Single-select stacked option cards.
export const OptionCards = ({ options, value, onChange, name }) => (
  <div className="flex flex-col gap-3" data-testid={`options-${name}`}>
    {options.map((o) => {
      const active = value === o.value;
      return (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          data-testid={`option-${name}-${o.value}`}
          className={`group flex items-center justify-between border px-6 py-5 text-left transition-colors duration-300 ${
            active
              ? "border-teal bg-teal/[0.06]"
              : "border-charcoal hover:border-teal/50"
          }`}
        >
          <span
            className={`font-sans text-base ${active ? "text-cream" : "text-cream/85"}`}
          >
            {o.label}
          </span>
          <span
            className={`flex h-5 w-5 items-center justify-center border transition-colors ${
              active ? "border-teal bg-teal text-ink" : "border-sand/50"
            }`}
          >
            {active && <Check size={13} strokeWidth={2.4} />}
          </span>
        </button>
      );
    })}
  </div>
);

// Compact segmented single-select (chips in a row).
export const Segmented = ({ options, value, onChange, name }) => (
  <div className="flex flex-wrap gap-3" data-testid={`segmented-${name}`}>
    {options.map((o) => {
      const active = value === o.value;
      return (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          data-testid={`segment-${name}-${o.value}`}
          className={`border px-5 py-3 font-sans text-sm transition-colors duration-300 ${
            active ? "border-teal bg-teal/[0.06] text-cream" : "border-charcoal text-cream/80 hover:border-teal/50"
          }`}
        >
          {o.label}
        </button>
      );
    })}
  </div>
);

// Multi-select pill chips.
export const ChipGroup = ({ options, values, onToggle, name }) => (
  <div className="flex flex-wrap gap-3" data-testid={`chips-${name}`}>
    {options.map((label) => {
      const active = values.includes(label);
      return (
        <button
          key={label}
          type="button"
          onClick={() => onToggle(label)}
          data-testid={`chip-${label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/(^-|-$)/g, "")}`}
          className={`rounded-full border px-5 py-2.5 font-sans text-sm transition-colors duration-300 ${
            active
              ? "border-teal bg-teal text-ink"
              : "border-charcoal text-cream/85 hover:border-teal/60"
          }`}
        >
          {label}
        </button>
      );
    })}
  </div>
);

export const TextField = ({ value, onChange, placeholder, type = "text", testId, ...rest }) => (
  <input
    type={type}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    data-testid={testId}
    className="w-full rounded-none border border-charcoal bg-charcoal/30 px-5 py-4 font-sans text-base text-cream placeholder:text-sand/70 transition-colors duration-300 focus:border-teal focus:outline-none"
    {...rest}
  />
);

export const TextArea = ({ value, onChange, placeholder, testId, rows = 4 }) => (
  <textarea
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    rows={rows}
    data-testid={testId}
    className="w-full resize-none rounded-none border border-charcoal bg-charcoal/30 px-5 py-4 font-sans text-base leading-relaxed text-cream placeholder:text-sand/70 transition-colors duration-300 focus:border-teal focus:outline-none"
  />
);

export const NativeSelect = ({ value, onChange, options, placeholder, testId }) => (
  <div className="relative">
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      className="w-full appearance-none rounded-none border border-charcoal bg-charcoal/30 px-5 py-4 font-sans text-base text-cream transition-colors duration-300 focus:border-teal focus:outline-none"
    >
      <option value="" disabled className="bg-ink">
        {placeholder}
      </option>
      {options.map((o) => (
        <option key={o} value={o} className="bg-ink text-cream">
          {o}
        </option>
      ))}
    </select>
    <span className="pointer-events-none absolute right-5 top-1/2 -translate-y-1/2 text-sand">
      ▾
    </span>
  </div>
);
